<template>
  <!-- HelpBar -->
  <HelpBar />

  <v-container class="share-project-view py-6" fluid>
    <v-row justify="center">
      <v-col cols="12" md="9" lg="7" xl="6">
        <v-card class="share-card" elevation="2">
          <!-- Header -->
          <div class="share-header" style="display: flex; align-items: center;">
            <div class="share-title-block">
              <v-btn
                icon
                variant="text"
                class="back-button"
                title="Zurück zum Projekt"
                aria-label="Zurück zum Projekt"
                @click="goBack"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>

              <div class="share-title-icon">
                <v-icon color="primary" size="28">
                  mdi-share-variant-outline
                </v-icon>
              </div>

              <div>
                <div class="text-h6 font-weight-bold">
                  Projektkopie senden
                </div>

                <div class="text-body-2 text-medium-emphasis">
                  Projektzugriff per E-Mail an eine weitere Person senden.
                </div>
              </div>
            </div>

            <v-spacer />

            <!-- Help button -->
            <v-tooltip text="Hilfe anzeigen" location="bottom" color="surface" content-class="tooltip" :disabled="store.sidebarShown">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  color="primary"
                  aria-label="Hilfe anzeigen"
                  @click="store.sidebarShown ? store.hideSidebar() : store.showSidebar(ShareProjectHelp)"
                >
                  <v-icon>{{ store.sidebarShown ? 'mdi-close' : 'mdi-help-circle-outline' }}</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>

          <v-divider />

          <!-- Main content -->
          <v-card-text class="share-card-content">
            <!-- Project summary -->
            <div
              v-if="projectName || projectId"
              class="project-summary"
            >
              <div class="project-summary-icon">
                <v-icon color="primary" size="24">
                  mdi-folder-outline
                </v-icon>
              </div>

              <div class="project-summary-content">
                <div class="text-caption text-medium-emphasis">
                  Ausgewähltes Projekt
                </div>

                <div class="text-subtitle-1 font-weight-bold">
                  {{ projectName || 'Projekt' }}
                </div>

                <div
                  v-if="projectId"
                  class="text-caption text-medium-emphasis"
                >
                  Projekt-ID: {{ projectId }}
                </div>
              </div>
            </div>

            <v-alert
              v-if="showSuccessHint"
              type="success"
              variant="tonal"
              border="start"
              class="share-message"
              closable
              @click:close="showSuccessHint = false"
            >
              <template #title>
                Projektkopie erfolgreich gesendet
              </template>

              Die Kopie liegt jetzt in der Projektliste der empfangenden Person.
              Eine E-Mail wird dabei nicht verschickt.
            </v-alert>

            <v-alert
              v-if="!senderEmail"
              type="warning"
              variant="tonal"
              border="start"
              class="share-message"
            >
              <template #title>
                Absenderadresse fehlt
              </template>

              Für die aktuelle Sitzung wurde keine E-Mail-Adresse gefunden.
              Bitte melden Sie sich erneut an.
            </v-alert>

            <v-form
              ref="formRef"
              class="share-form"
              @submit.prevent="onSubmit"
            >
              <div class="form-section">
                <div class="form-section-heading">
                  <v-icon size="22" color="primary">
                    mdi-email-send-outline
                  </v-icon>

                  <span>Empfänger</span>
                </div>

                <!-- Recipient Email -->
                <v-text-field
                  v-model.trim="recipientEmail"
                  label="E-Mail-Adresse des Empfängers"
                  type="email"
                  :rules="emailRules"
                  :disabled="submitting"
                  clearable
                  autofocus
                  prepend-inner-icon="mdi-email-outline"
                  autocomplete="email"
                  validate-on="blur"
                  class="mb-3"
                />

                <div class="field-description text-body-2 text-medium-emphasis">
                  Diese Person erhält eine Einladung zum Zugriff auf das Projekt.
                </div>
              </div>

              <div class="form-section">
                <div class="form-section-heading">
                  <v-icon size="22" color="primary">
                    mdi-account-circle-outline
                  </v-icon>

                  <span>Absender</span>
                </div>

                <!-- Sender Email -->
                <v-text-field
                  :model-value="senderEmail"
                  label="Ihre E-Mail-Adresse"
                  type="email"
                  readonly
                  prepend-inner-icon="mdi-account-circle-outline"
                  class="mb-1"
                />

                <div class="field-description text-body-2 text-medium-emphasis">
                  Die Einladung wird in Ihrem Namen versendet.
                </div>
              </div>

              <div class="form-section">
                <div class="form-section-heading">
                  <v-icon size="22" color="primary">
                    mdi-note-edit-outline
                  </v-icon>

                  <span>Bemerkung</span>
                </div>

                <!-- Bemerkung (optional) -->
                <v-textarea
                  v-model="note"
                  label="Bemerkung hinzufügen (optional)"
                  :disabled="submitting"
                  rows="3"
                  auto-grow
                  clearable
                  prepend-inner-icon="mdi-note-edit-outline"
                  class="mb-1"
                />

                <div class="field-description text-body-2 text-medium-emphasis">
                  Die Bemerkung wird am Projekt gespeichert und erscheint auch in der Projektliste des Empfängers.
                </div>
              </div>
            </v-form>
          </v-card-text>

          <v-divider />

          <!-- Footer actions -->
          <div class="share-footer">
            <v-btn
              variant="text"
              :disabled="submitting"
              @click="goBack"
            >
              Abbrechen
            </v-btn>

            <v-btn
              color="primary"
              variant="elevated"
              :loading="submitting"
              :disabled="!canSubmit"
              class="share-button"
              @click="onSubmit"
            >
              <v-icon start>mdi-send</v-icon>
              Projektkopie senden
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/plugins/axios'
import { useProjectStore } from '@/stores/projectStore'
import HelpBar from '@/components/HelpBar.vue'
import ShareProjectHelp from '@/components/help/ShareProjectHelp.vue'

// Used by Vue
const router = useRouter()
const route = useRoute()
const store = useProjectStore()

// Make sure user info is loaded
onMounted(async () => {
  // Pre-select help for this view so fallback help doesn't render
  store.setHelpComponent(ShareProjectHelp)

  await store.init?.()

  if (
    projectId.value &&
    store.currentProject?.id !== projectId.value
  ) {
    await store.fetchCurrentProject(projectId.value)
  }

  // Pre-fill the existing Bemerkung (note) from the loaded project
  note.value = store.currentProject?.note ?? ''

  if (projectName.value) {
    store.setTitle(projectName.value)
  }
})

// Props are optional; fallback to route params
const props = defineProps({
  projectId: Number,
  projectName: String,
})

// Resolve projectId
const projectId = computed(() => {
  if (props.projectId) return props.projectId
  const fromRoute = Number(route.params.projectId)
  return isFinite(fromRoute) ? fromRoute : null
})

// Resolve projectName
const projectName = computed(() => {
  // If parent passed the name
  if (props.projectName) return props.projectName

  // Prefer the store's loaded project
  if (store.currentProject?.name) return store.currentProject.name

  return ''
})


// Sender (current logged‑in user)
const senderEmail = computed(() => store.user?.email || '')

// Form data
const recipientEmail = ref('')
const note = ref('')
const submitting = ref(false)
const showSuccessHint = ref(false)
const formRef = ref(null)

// Validation
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const emailRules = [
  value => !!value || 'Bitte geben Sie eine E-Mail-Adresse ein.',
  value =>
    emailRegex.test(value) ||
    'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
]

const canSubmit = computed(() => {
  return Boolean(
    recipientEmail.value &&
    senderEmail.value &&
    projectId.value &&
    emailRegex.test(recipientEmail.value)
  )
})

// Go back to dashboard
function goBack() {
  if (projectId.value) {
    router.push({ name: 'ProjectDashboard', params: { projectId: projectId.value } })
  } else {
    router.back()
  }
}

// Submit the form
async function onSubmit() {
  if (submitting.value) return

  const validationResult = await formRef.value?.validate?.()

  if (
    validationResult &&
    validationResult.valid === false
  ) {
    toast.warning(
      'Bitte geben Sie eine gültige Empfängeradresse ein.'
    )
    return
  }

  if (!canSubmit.value) {
    toast.warning(
      'Bitte geben Sie eine gültige Empfängeradresse ein.'
    )
    return
  }

  submitting.value = true
  showSuccessHint.value = false

  try {
    // Save the (possibly edited) Bemerkung on the sender's own project first.
    // The share endpoint clones the whole project row, so the recipient's copy
    // then inherits this note automatically (both lists show it).
    await api.patch(`/projects/${projectId.value}/`, { note: note.value })

    const payload = {
      senderEmail: senderEmail.value,
      email: recipientEmail.value,
      projectId: projectId.value,
    }

    await api.post(
      `/projects/${projectId.value}/share/`,
      payload
    )

    toast.success('Projektkopie wurde erfolgreich gesendet.')

    showSuccessHint.value = true
    recipientEmail.value = ''

    formRef.value?.resetValidation?.()
  } catch (err) {
    const errorData = err.response?.data

    const serializedError = JSON.stringify(
      errorData ?? {}
    )

    const isDraftActionError =
      errorData?.code === 'DRAFT_ACTION_EXISTS' ||
      errorData?.error?.includes?.(
        'DRAFT_ACTION_EXISTS'
      ) ||
      serializedError.includes(
        'DRAFT_ACTION_EXISTS'
      )

    if (isDraftActionError) {
      toast.warning(
        'Das Projekt enthält mindestens eine Maßnahme im Entwurfsstatus und kann daher nicht gesendet werden.'
      )
    } else {
      const message =
        errorData?.error ||
        errorData?.detail ||
        'Die Projektkopie konnte nicht gesendet werden.'

      toast.error(message)
    }
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped lang="scss">
.share-project-view {
  min-height: 100%;
}

.share-card {
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 28px;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
}

/* ========================================
   Header
======================================== */

.share-header {
  padding: 1.25rem 1.5rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.share-title-block {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.back-button {
  flex-shrink: 0;
}

.share-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* ========================================
   Main content
======================================== */

.share-card-content {
  display: grid;
  gap: 1.25rem;

  padding: 1.5rem;
}

.project-summary {
  display: flex;
  align-items: center;
  gap: 0.9rem;

  padding: 1rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 22px;

  background-color: rgba(var(--v-theme-primary), 0.045);
}

.project-summary-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 44px;
  height: 44px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.09);
}

.project-summary-content {
  min-width: 0;
}

.share-message {
  border-radius: 20px;
}

/* ========================================
   Form
======================================== */

.share-form {
  display: grid;
  gap: 1.25rem;
}

.form-section {
  padding: 1rem;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;

  background-color: rgba(var(--v-theme-primary), 0.025);
}

.form-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

.share-form :deep(.v-field) {
  border-radius: 18px;
}

.field-description {
  padding: 0 0.5rem;
  line-height: 1.45;
}

/* ========================================
   Footer
======================================== */

.share-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;

  padding: 1rem 1.5rem;
}

.share-footer .v-btn {
  font-weight: 600;
  text-transform: none;
}

.share-button {
  min-width: 160px;
}

/* ========================================
   Responsive
======================================== */

@media (max-width: 700px) {
  .share-project-view {
    padding-inline: 0.5rem;
  }

  .share-header {
    padding: 1rem;
  }

  .share-title-block {
    align-items: flex-start;
  }

  .share-title-icon {
    width: 42px;
    height: 42px;
  }

  .share-card-content {
    padding: 1rem;
  }

  .form-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .share-footer {
    align-items: stretch;
    flex-direction: column-reverse;

    padding: 1rem;
  }

  .share-footer .v-btn {
    width: 100%;
  }

  .share-button {
    min-width: 0;
  }
}
</style>